import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icono } from './Iconos';

// Tema compartido "Contador Nixie". Los colores salen de las variables CSS de
// theme.css; así las pantallas aún no migradas (bloque .legacy-light) reciben
// los mismos componentes en claro sin tocar su código.

// Colores por rol afinados para acero (hex: algunas pantallas les concatenan opacidad)
export const ROL_COLORS   = { admin: '#6d28d9', finanzas: '#0369a1', solicitante: '#15803d', director: '#b45309', contador: '#0f766e', programacion: '#be185d', jefe_compras: '#b45309', comprador: '#4338ca' };
// El texto de cada estado es el mismo tono del punto: sobre acero se lee claro,
// y en .legacy-light las variables --st-* ya traen la versión oscura.
const estado = (v, rgb, label) => ({ bg: `rgba(${rgb},0.12)`, border: `rgba(${rgb},0.35)`, text: `var(${v})`, dot: `var(${v})`, label });
export const ESTADO_META  = {
  pendiente: estado('--st-pendiente', '245,207,74', 'Validación Contable'),
  aprobada:  estado('--st-aprobada', '94,224,143', 'Programación de pago'),
  por_pagar: estado('--st-por-pagar', '76,195,245', 'Por pagar'),
  rechazada: estado('--st-rechazada', '255,122,122', 'Rechazada'),
  pagada:    estado('--st-pagada', '143,178,255', 'Pagada'),
};
export const ROL_LABELS   = { admin: 'Admin', finanzas: 'Finanzas', solicitante: 'Solicitante', director: 'Director', contador: 'Contador', programacion: 'Programación', jefe_compras: 'Jefe de Compras', comprador: 'Comprador' };
// Hex (no variables) porque los historiales les concatenan opacidad: `${c}18`.
// Afinados para leerse sobre acero; sus únicos consumidores ya están migrados.
export const ESTADO_COLORS = { pendiente: '#b45309', aprobada: '#15803d', por_pagar: '#0369a1', rechazada: '#be123c', pagada: '#1d4ed8' };

export function opcionesEstado(rol, item) {
  const TODOS_ESTADOS = ['pendiente', 'aprobada', 'por_pagar', 'rechazada', 'pagada'];
  if (rol === 'admin') return TODOS_ESTADOS;
  if (rol === 'contador')
    return item.estado === 'pendiente' ? ['pendiente', 'aprobada', 'rechazada'] : null;
  if (rol === 'programacion') {
    const operable = item.flujo !== 'v2'
      ? item.estado !== 'pagada'
      : ['aprobada', 'por_pagar'].includes(item.estado);
    return operable ? [...new Set([item.estado, 'por_pagar', 'pagada', 'rechazada'])] : null;
  }
  return null;
}
export const ESTADO_OPCION_LABEL = { pendiente: 'Validación Contable', por_pagar: 'por pagar', aprobada: 'programación de pago', rechazada: 'Rechazada', pagada: 'Pagada' };
export const ESTADO_LABEL = { pendiente: 'Validación Contable', aprobada: 'Programación de pago', por_pagar: 'Por pagar', rechazada: 'Rechazada', pagada: 'Pagada' };

// Colores que en el tema anterior significaban "acción principal" o "neutro":
// se traducen a la placa naranja o a la placa gris. El resto (verde de aprobar,
// rojo de rechazar…) conserva su color porque ahí el color es el significado.
const PRIMARIOS = ['#5e72e4', '#ff541c', '#FF541C', '#f47920', '#F47920']; // #f47920: naranja anterior a la marca
const NEUTROS = ['#6b7280', '#44475a', '#747878', '#525f7f', '#66758a'];

const PLACA = {
  padding: '8px 18px', borderRadius: 4,
  fontFamily: 'var(--btn-font)', fontSize: 'var(--btn-size)', fontWeight: 600,
  letterSpacing: 'var(--btn-track)', textTransform: 'var(--btn-case)',
  cursor: 'pointer', lineHeight: 1.2,
};

export const css = {
  input: (err) => ({
    width: '100%', boxSizing: 'border-box',
    padding: '9px 12px', borderRadius: 4,
    border: `1px solid ${err ? 'var(--danger)' : 'var(--mesh)'}`,
    fontSize: 13.5, fontFamily: 'Inter, sans-serif',
    background: err ? 'var(--danger-bg)' : 'var(--well)',
    color: 'var(--ink)', outline: 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  }),
  inputRO: { background: 'var(--panel-hi)', color: 'var(--glass)', cursor: 'default' },
  select: (err) => ({
    width: '100%', boxSizing: 'border-box',
    padding: '9px 32px 9px 12px', borderRadius: 4,
    border: `1px solid ${err ? 'var(--danger)' : 'var(--mesh)'}`,
    fontSize: 13.5, fontFamily: 'Inter, sans-serif',
    background: err ? 'var(--danger-bg)' : 'var(--well)',
    color: 'var(--ink)', outline: 'none',
    appearance: 'none', cursor: 'pointer',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24'%3E%3Cpath fill='%238a9098' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  }),
  card: {
    boxShadow: 'var(--card-shadow)',
    background: 'var(--screws), var(--panel)',
    borderRadius: 6,
    border: '1px solid var(--mesh)',
  },
  // Botón ghost: placa gris
  btnGhost: {
    ...PLACA,
    background: 'var(--ghost-bg)', border: '1px solid var(--ghost-line)', color: 'var(--ghost-fg)',
  },
  btn: (color = '#5e72e4', outlined = false) => {
    if (PRIMARIOS.includes(color)) {
      return outlined
        ? { ...PLACA, background: 'transparent', border: '1px solid var(--plasma-line)', color: 'var(--plasma)' }
        : { ...PLACA, background: 'var(--btn-bg)', border: '1px solid var(--btn-line)', color: 'var(--btn-fg)', boxShadow: 'var(--btn-shadow)' };
    }
    if (NEUTROS.includes(color)) {
      return { ...PLACA, background: 'var(--ghost-bg)', border: '1px solid var(--ghost-line)', color: 'var(--ghost-fg)' };
    }
    return {
      ...PLACA,
      background: outlined ? 'transparent' : color,
      border: `1px solid ${color}`,
      color: outlined ? color : '#ffffff',
    };
  },
};

export const Label = ({ children, required }) => (
  <label className="nx-label" style={{ display: 'block', marginBottom: 6 }}>
    {children}{required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
  </label>
);

// Destino del pago (a quién y a qué cuenta) en el formulario de "corregir y
// reenviar": se muestra pero NO se edita. Cambiarlo mandaría el dinero a otro
// lado con un folio que el aprobador ya había visto, así que el servidor lo
// rechaza (backend/src/utils/datosBancarios.js) y aquí ni siquiera se ofrece.
// `campos` es una lista de [etiqueta, valor].
export const DestinoPagoFijo = ({ campos = [] }) => (
  <div style={{ borderRadius: 4, border: '1px solid var(--mesh)', background: 'var(--well)', padding: '10px 12px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--plasma)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
      <span className="nx-label">Destino del pago (no se puede cambiar)</span>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px 12px' }}>
      {campos.filter(([, v]) => v).map(([etiqueta, valor]) => (
        <div key={etiqueta} style={{ fontSize: 12.5, minWidth: 0 }}>
          <span style={{ color: 'var(--glass)' }}>{etiqueta}: </span>
          <span title={String(valor)} style={{ color: 'var(--ink)', fontWeight: 600, fontFamily: /CLABE|Cuenta/i.test(etiqueta) ? 'var(--digit-font)' : 'inherit', wordBreak: 'break-word' }}>{valor}</span>
        </div>
      ))}
    </div>
    <div style={{ fontSize: 12, color: 'var(--glass)', marginTop: 8 }}>
      Para pagar a otra cuenta o a otro beneficiario, corrige el catálogo y crea una solicitud nueva: así vuelve a pasar por el flujo de aprobación completo.
    </div>
  </div>
);

export const Grid = ({ cols = 1, gap = 14, children, style }) => (
  <div className="ui-grid" style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap, ...style }}>
    {children}
  </div>
);

export function formatMiles(raw) {
  if (raw === '' || raw == null) return '';
  let s = String(raw);
  const neg = s.startsWith('-');
  if (neg) s = s.slice(1);
  const dot = s.indexOf('.');
  let ent = dot >= 0 ? s.slice(0, dot) : s;
  const dec = dot >= 0 ? s.slice(dot + 1) : null;
  ent = ent.replace(/^0+(?=\d)/, '');
  if (ent === '') ent = '0';
  ent = ent.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (neg ? '-' : '') + ent + (dec !== null ? '.' + dec : '');
}

export function limpiarNumero(text) {
  let s = String(text).replace(/[^\d.]/g, '');
  const i = s.indexOf('.');
  if (i >= 0) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
  return s;
}

export function MontoInput({ value, onChange, err, style, placeholder = '0.00' }) {
  return (
    <input
      type="text" inputMode="decimal"
      value={formatMiles(value)}
      onChange={e => onChange(limpiarNumero(e.target.value))}
      placeholder={placeholder}
      style={{ ...css.input(err), fontFamily: 'var(--digit-font)', ...style }}
    />
  );
}

export const SectionHeader = ({ children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    margin: '22px 0 12px', paddingBottom: 8,
    borderBottom: '1px solid var(--mesh-soft)',
  }}>
    <span className="nx-label" style={{ color: 'var(--plasma)' }}>{children}</span>
  </div>
);

export const Badge = ({ children, color = 'var(--glass)', bg }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center',
    padding: '2px 8px', borderRadius: 3,
    fontSize: 11.5, fontWeight: 600,
    color, background: bg || 'var(--panel-hi)',
    border: '1px solid var(--mesh-soft)',
  }}>{children}</span>
);

export const Toast = ({ msg, tipo, onClose }) => msg ? (
  <div role="status" onClick={onClose} style={{
    position: 'fixed', top: 20, right: 20, zIndex: 9999, cursor: 'pointer',
    background: 'var(--panel)',
    border: `1px solid ${tipo === 'error' ? 'var(--danger-line)' : 'rgba(21,128,61,0.45)'}`,
    color: 'var(--ink)',
    padding: '12px 16px 12px 14px', borderRadius: 4,
    fontSize: 13, fontWeight: 500,
    boxShadow: '0 10px 30px rgba(17,17,17,0.14)',
    maxWidth: 380, minWidth: 240,
    display: 'flex', alignItems: 'center', gap: 10,
    animation: 'slideInRight 0.2s ease',
  }}>
    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: tipo === 'error' ? 'var(--danger)' : 'var(--ok)', boxShadow: `0 0 8px ${tipo === 'error' ? 'rgba(190,18,60,.7)' : 'rgba(21,128,61,.7)'}` }} />
    <span>{msg}</span>
    <style>{`@keyframes slideInRight { from { opacity:0; transform:translateX(16px); } to { opacity:1; transform:translateX(0); } }`}</style>
  </div>
) : null;

// Pila de modales abiertos: Esc y el atrapado de Tab solo actúan sobre el de
// arriba, para que cerrar un modal encimado no cierre también el de abajo.
const pilaModales = [];
const ENFOCABLES = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, title, children, width = 480, zIndex = 1000 }) {
  const caja = useRef(null);
  const cerrar = useRef(onClose);
  cerrar.current = onClose;
  const idTitulo = useId();

  // Teclado: Esc cierra, Tab no se sale del modal, el foco entra al abrir y
  // vuelve al botón que lo abrió al cerrar.
  useEffect(() => {
    if (!open) return undefined;
    const previo = document.activeElement;
    const yo = {};
    pilaModales.push(yo);
    const t = setTimeout(() => {
      const c = caja.current;
      if (!c) return;
      const campo = c.querySelector('.nx-modal-cuerpo input:not([disabled]), .nx-modal-cuerpo select:not([disabled]), .nx-modal-cuerpo textarea:not([disabled])');
      (campo || c).focus();
    }, 0);
    const tecla = (e) => {
      if (pilaModales[pilaModales.length - 1] !== yo) return;
      if (e.key === 'Escape') {
        if (e.defaultPrevented) return; // un control interno (p. ej. un desplegable) ya lo atendió
        e.preventDefault();
        cerrar.current?.();
        return;
      }
      if (e.key !== 'Tab' || !caja.current) return;
      const els = [...caja.current.querySelectorAll(ENFOCABLES)].filter(el => el.offsetParent !== null);
      if (!els.length) { e.preventDefault(); return; }
      const primero = els[0], ultimo = els[els.length - 1];
      if (e.shiftKey && (document.activeElement === primero || document.activeElement === caja.current)) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener('keydown', tecla);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', tecla);
      pilaModales.splice(pilaModales.indexOf(yo), 1);
      if (previo && typeof previo.focus === 'function' && document.contains(previo)) previo.focus();
    };
  }, [open]);

  if (!open) return null;
  // Se dibuja en <body>: dentro del área principal quedaría debajo del menú
  // lateral (el main crea su propia capa de apilamiento).
  return createPortal(
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0,
      background: 'rgba(17,17,17,0.45)',
      zIndex, display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 20,
      animation: 'modalBgIn 0.15s ease',
    }}>
      <div ref={caja} role="dialog" aria-modal="true" aria-labelledby={idTitulo} tabIndex={-1} onClick={e => e.stopPropagation()} style={{
        outline: 'none',
        background: 'var(--screws), var(--panel)', border: '1px solid var(--mesh)',
        borderRadius: 6, width, maxWidth: '100%',
        maxHeight: '92vh', overflowY: 'auto',
        boxShadow: '0 24px 60px rgba(17,17,17,0.18)', color: 'var(--ink)',
        animation: 'modalIn 0.18s ease',
      }}>
        <div style={{
          padding: '14px 20px 14px 24px', borderBottom: '1px solid var(--mesh-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <span id={idTitulo} style={{ fontFamily: 'var(--label-font)', fontSize: 17, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink)' }}>{title}</span>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="nx-ghost" style={{
            fontSize: 16, width: 30, height: 30,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}><Icono nombre="cerrar" size={15} /></button>
        </div>
        <div className="nx-modal-cuerpo" style={{ padding: '20px 24px' }}>{children}</div>
      </div>
      <style>{`
        @keyframes modalBgIn { from { opacity:0; } to { opacity:1; } }
        @keyframes modalIn   { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      `}</style>
    </div>
  , document.body);
}

export function PageHeader({ icon, title, subtitle, actions }) {
  return (
    <div style={{
      padding: '0 0 14px', marginBottom: 20,
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
      borderBottom: '1px solid var(--mesh-soft)',
      gap: 12, flexWrap: 'wrap',
    }}>
      <div>
        <h1 style={{ margin: 0, fontFamily: 'var(--label-font)', fontSize: 24, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.1, letterSpacing: '.05em', textTransform: 'uppercase' }}>{title}</h1>
        {subtitle && <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--glass)' }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

export function EstadoBadge({ estado }) {
  const m = ESTADO_META[estado] || { bg: 'var(--panel-hi)', border: 'var(--mesh)', text: 'var(--glass)', dot: 'var(--steel)', label: estado };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 8px', borderRadius: 3, fontSize: 11.5, fontWeight: 600,
      background: m.bg, color: m.text, border: `1px solid ${m.border}`, whiteSpace: 'nowrap',
    }}>
      <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

export function IconBtn({ onClick, title, icon, danger, disabled }) {
  return (
    <button type="button" onClick={onClick} title={title} aria-label={title} disabled={disabled} className="db-icon-btn"
      style={{ opacity: disabled ? 0.4 : 1, fontSize: 13, ...(danger ? { color: 'var(--danger)', borderColor: 'var(--danger-line)' } : {}) }}>
      {icon}
    </button>
  );
}

export function Paginacion({ page, pages, onPage }) {
  if (!pages || pages <= 1) return null;
  const btn = (label, target, disabled, title) => (
    <button type="button" onClick={() => onPage(target)} disabled={disabled} title={title} aria-label={title}
      className="nx-ghost"
      style={{ padding: '5px 11px', fontSize: 13, fontWeight: 600, opacity: disabled ? 0.35 : 1 }}>
      {label}
    </button>
  );
  return (
    <div style={{ padding: '12px 20px', borderTop: '1px solid var(--mesh-soft)', display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
      {btn('«', 1, page <= 1, 'Primera página')}
      {btn('‹', page - 1, page <= 1, 'Página anterior')}
      <span style={{ fontSize: 12.5, color: 'var(--glass)', padding: '0 8px', fontFamily: 'var(--digit-font)' }}>
        {page} / {pages}
      </span>
      {btn('›', page + 1, page >= pages, 'Página siguiente')}
      {btn('»', pages, page >= pages, 'Última página')}
    </div>
  );
}

export function Spinner() {
  return (
    <div role="status" style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:60, gap:14 }}>
      <div style={{ position:'relative', width:36, height:36 }}>
        <div style={{ position:'absolute', inset:0, border:'2px solid var(--mesh)', borderRadius:'50%' }} />
        <div style={{
          position:'absolute', inset:0,
          border:'2px solid transparent', borderTopColor:'var(--plasma)', borderRadius:'50%',
          filter: 'drop-shadow(0 0 4px rgba(255,84,28,.6))',
          animation:'spin 0.7s cubic-bezier(0.4,0,0.2,1) infinite',
        }} />
      </div>
      <span className="nx-label">Cargando…</span>
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
}

// Cifra en tubos nixie: cada dígito en su tubo encendido, la puntuación sin
// tubo. Firma del tema: cuando el valor cambia, la cifra anterior se apaga
// encima mientras la nueva se enciende debajo (un cross-fade por actualización).
function Cifra({ s, tubos, size }) {
  if (!tubos) return <span className="nx-plain" style={{ fontSize: size }}>{s}</span>;
  return (
    <span className="nx-digits" style={{ fontSize: size }}>
      {s.split('').map((c, i) => /\d/.test(c)
        ? <span key={i} className="nx-tube">{c}</span>
        : <span key={i} className="nx-sep">{c}</span>)}
    </span>
  );
}

// Variante "viva" para lecturas que cambian solas (reloj, barrido de cátodos):
// solo el tubo cuyo dígito cambió se vuelve a encender; los demás siguen quietos.
export function NixieVivo({ value, size = 28, label }) {
  const s = String(value);
  return (
    <span className="nx-digits nx-vivo" style={{ fontSize: size }} role="img" aria-label={label || s}>
      {s.split('').map((c, i) => /\d/.test(c)
        ? <span key={`${i}-${c}`} className="nx-tube" aria-hidden="true">{c}</span>
        : <span key={`${i}-sep`} className="nx-sep" aria-hidden="true">{c}</span>)}
    </span>
  );
}

export function Nixie({ value, size = 28, tubos = true, label, alerta = false }) {
  const s = String(value);
  const previo = useRef(s);
  const [saliente, setSaliente] = useState(null);
  useEffect(() => {
    if (previo.current === s) return undefined;
    setSaliente(previo.current);
    previo.current = s;
    const t = setTimeout(() => setSaliente(null), 600);
    return () => clearTimeout(t);
  }, [s]);
  return (
    <span className={`nx-stack${alerta ? ' nx-alerta' : ''}`} role="img" aria-label={label || s}>
      <span key={s} className={saliente ? 'nx-ignite' : undefined} aria-hidden="true"><Cifra s={s} tubos={tubos} size={size} /></span>
      {saliente && <span className="nx-fade" aria-hidden="true"><Cifra s={saliente} tubos={tubos} size={size} /></span>}
    </span>
  );
}
